// https://www.figma.com/file/q7KlcLyYFswO2QB3I4BWmE/Interests-Graph-on-Web?node-id=4%3A2095
import { useState, useEffect } from 'react'
import { QueryClient, QueryClientProvider, useQuery } from 'react-query'
import { useLocation } from 'react-router-dom'
import { cx, css, useTheme } from '@fable/theme'
import ProfileTabs from './ProfileTabs'
import ProfileClubs from './ProfileClubs'
import {
  BookReview,
  BookReviewModal,
  FlexBox,
  Loader,
  QuoteView,
  QuoteViewModal,
} from '@fable/components'
import { Quote, Review, User } from '@fable/types'
import UserBookLists from '../../../../components/user_book_lists'
import ProfileBaseView from './ProfileBaseView'
import { useActions, useTypedSelector, useUser } from '../../../../hooks'
import { routes as marketingRoutes } from '../../../marketing/routes'
import { QuoteReview } from '../../pages/review_page/ReviewPage'
import { GIFTS_ENABLED } from 'app/store/constants'
import { isUrlMatched } from 'utils'
import { routes as socialRoutes } from 'app/social/routes'
import { ProfileTab } from './types'
import ProfilePosts from './ProfilePosts'
import { requests } from '@fable/api'
import StatsProfilePage from 'app/webview/pages/stats_profile/StatsProfilePage'

const ProfileContentPublicConsumer: React.VFC = () => {
  const location = useLocation()
  const { colors, motion } = useTheme()
  const { user: authUser } = useUser()
  const { user } = useTypedSelector(({ social }) => social)

  const { openModal, copyTextToClipboard } = useActions()
  const [activeTab, setActiveTab] = useState<ProfileTab>('lists')
  const [statsInitiated, setStatsInitiated] = useState(false)
  const [clubsInitiated, setClubsInitiated] = useState(false)
  const [reviewsInitiated, setReviewsInitiated] = useState(false)
  const [postsInitiated, setPostsInitiated] = useState(false)
  const [quotesInitiated, setQuotesInitiated] = useState(false)
  const [showStats, setShowStats] = useState(false)

  const authUserId = authUser?.id || ''
  const userId = user?.data?.id || ''
  const isViewingSelf = userId === authUserId

  const { isFetching } = useQuery(
    ['user', userId],
    () => requests.getUser(userId || ''),
    {
      enabled: !!userId?.length,
      onSuccess: ({ data }: { data: User }) => {
        const show = isViewingSelf || !!data.is_stats_public
        setShowStats(show)
        setActiveTab(show ? 'stats' : 'lists')
      },
    }
  )

  const showGiftBookButton = (item: QuoteReview) =>
    GIFTS_ENABLED &&
    !!item?.book?.can_purchase &&
    !item.book?.is_free &&
    !isUrlMatched(`${socialRoutes.bookList}/:id`, location.pathname, true)

  useEffect(() => {
    if (!statsInitiated && activeTab === 'stats') {
      setStatsInitiated(true)
    }
    if (!clubsInitiated && activeTab === 'clubs') {
      setClubsInitiated(true)
    }
    if (!reviewsInitiated && activeTab === 'reviews') {
      setReviewsInitiated(true)
    }
    if (!postsInitiated && activeTab === 'posts') {
      setPostsInitiated(true)
    }
    if (!quotesInitiated && activeTab === 'quotes') {
      setQuotesInitiated(true)
    }
  }, [
    activeTab,
    statsInitiated,
    clubsInitiated,
    reviewsInitiated,
    postsInitiated,
    quotesInitiated,
  ])

  const renderContent = () => {
    const getDisplayStyle = (tab: string) =>
      css`
        width: ${tab === 'lists' ? 'auto' : '100%'};
        display: ${activeTab === tab ? 'block' : 'none'};
      `

    return (
      <>
        {showStats && (
          <div className={getDisplayStyle('stats')}>
            {/* if user has stats, this becomes the default page */}
            {statsInitiated && (
              <div
                className={css`
                  margin: 0 auto;
                  width: 100%;
                  max-width: 420px;
                  border-radius: 32px;
                  overflow: hidden;
                `}
              >
                {!!userId?.length ? (
                  <StatsProfilePage
                    userId={userId}
                    isSelfView={isViewingSelf}
                    isWeb
                    forceCustomGoals
                  />
                ) : null}
              </div>
            )}
          </div>
        )}
        <div className={getDisplayStyle('lists')}>
          {/* this is the default page */}
          {/* pagination and API call is handled by UserBookLists (it's an older component) */}
          <UserBookLists />
        </div>
        <div className={getDisplayStyle('clubs')}>
          {/* This allows the component to launch for the first time with an animation
          and fetch data only when it's rendered. Then it'll stay rendered with data cached. */}
          {clubsInitiated && <ProfileClubs profileUserId={userId} />}
        </div>
        <div className={getDisplayStyle('reviews')}>
          {reviewsInitiated && (
            <ProfileBaseView
              queryArgs={['reviews', userId]}
              apiUrl={`/users/${userId}/reviews`}
              renderChild={(item: QuoteReview, isSingleItem?: boolean) => (
                <div
                  onClick={() =>
                    openModal(
                      <BookReviewModal
                        showGiftBookButton={showGiftBookButton(item)}
                        onCopyToClipboard={copyTextToClipboard}
                        review={item as Review}
                      />
                    )
                  }
                  className={css`
                    cursor: pointer;
                    ${motion.transition()};
                    &:hover {
                      ${motion.moveY(-15)}
                    }
                    > div {
                      ${isSingleItem
                        ? 'max-width: 500px; margin: 0 auto;'
                        : ''};
                      height: 277px;
                      overflow: hidden;
                      border-radius: 18px;
                    }
                  `}
                >
                  <BookReview
                    review={item as Review}
                    isPreview={true}
                    showGiftBookButton={showGiftBookButton(item)}
                    onCopyToClipboard={copyTextToClipboard}
                  />
                </div>
              )}
            />
          )}
        </div>
        <div className={getDisplayStyle('posts')}>
          {postsInitiated && <ProfilePosts profileUserId={userId} />}
        </div>
        <div className={getDisplayStyle('quotes')}>
          {quotesInitiated && (
            <ProfileBaseView
              queryArgs={['quotes', userId]}
              apiUrl={`/users/${userId}/quotes`}
              renderChild={(item: QuoteReview, isSingleItem?: boolean) => {
                const quote = item as Quote

                return (
                  <div
                    onClick={() =>
                      openModal(
                        <QuoteViewModal
                          quote={quote}
                          downloadLink={marketingRoutes.download}
                        />
                      )
                    }
                    className={css`
                      cursor: pointer;
                      ${motion.transition()};
                      &:hover {
                        ${motion.moveY(-15)}
                      }
                      > div {
                        ${isSingleItem ? 'margin: 0 auto;' : ''};
                      }
                    `}
                  >
                    <QuoteView
                      text={quote.text}
                      book={quote.book}
                      theme={quote.theme}
                    />
                  </div>
                )
              }}
            />
          )}
        </div>
      </>
    )
  }

  if (isFetching) return <Loader />

  return (
    <>
      <FlexBox justifyContent="center">
        <ProfileTabs
          onChangeTab={setActiveTab}
          activeTab={activeTab}
          showStats={showStats}
        />
      </FlexBox>
      <div
        className={cx(
          'wrap',
          css`
            padding: 41px 0px;
            background: ${colors.almostWhite};
            min-height: 500px;
          `
        )}
      >
        <FlexBox
          justifyContent="center"
          className={css`
            max-width: 896px;
            margin: 0px auto;
          `}
        >
          {renderContent()}
        </FlexBox>
      </div>
    </>
  )
}

const queryClient = new QueryClient()

const ProfileContentPublic = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ProfileContentPublicConsumer />
    </QueryClientProvider>
  )
}

export default ProfileContentPublic
